import {
  Button,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
  CircularProgress,
  Chip,
  Pagination,
  Stack,
  Box,
  Alert,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Tooltip,
  Fade,
  InputLabel
} from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { 
  FilterList, 
  Payment, 
  AccountBalanceWallet, 
  Refresh, 
  Warning, 
  Info,
  Favorite,
  FavoriteBorder,
  Delete,
  RemoveCircle,
  Close,
  CheckCircle,
  Cancel,
  ArrowDropDown
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// UPDATED: Import Search component like Events page
import Search from "../../../components/common/search/Search";

// UPDATED: Use the same axios instance pattern as Events page
import axiosInstance from "../../../utils/axios/axiosInstance";

// UPDATED: Use react-toastify like Events page
import { toast } from 'react-toastify';

// UPDATED: Import loading gif like Events page
import loadingGif from '../../../assets/images/loading.gif';

const PLACEHOLDER_IMAGE = "https://via.placeholder.com/150";

// UPDATED: Simplified Remove Button Component using Events page button style
const RemoveButton = React.memo(({ onRemove, itemName, variant = "outlined", size = "small", fullWidth = false }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemoveClick = useCallback((e) => {
    e.stopPropagation();
    setShowConfirm(true);
  }, []);

  const handleConfirmRemove = useCallback(async (e) => {
    e.stopPropagation();
    setIsRemoving(true);
    
    try {
      await onRemove();
      setShowConfirm(false);
    } catch (error) {
      console.error('Remove failed:', error);
    } finally {
      setIsRemoving(false);
    }
  }, [onRemove]);

  const handleCancelRemove = useCallback((e) => {
    e.stopPropagation();
    setShowConfirm(false);
  }, []);

  if (showConfirm) {
    return (
      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        flexDirection: fullWidth ? 'column' : 'row'
      }}>
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={handleConfirmRemove}
          disabled={isRemoving}
          startIcon={isRemoving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          className="!min-w-fit !px-3 !h-8"
          fullWidth={fullWidth}
        >
          {isRemoving ? 'Removing...' : 'Confirm'}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={handleCancelRemove}
          disabled={isRemoving}
          startIcon={<Cancel />}
          className="!min-w-fit !px-3 !h-8"
          fullWidth={fullWidth}
        >
          Cancel
        </Button>
      </Box>
    );
  }

  return (
    <Button
      variant="outlined"
      color="error"
      size="small"
      onClick={handleRemoveClick}
      startIcon={<RemoveCircle />}
      className="!w-full !px-3 !h-8"
      fullWidth={fullWidth}
    >
      Remove
    </Button>
  );
});

// Helper function to truncate title
const truncateTitle = (title, maxLength = 25) => {
  if (!title) return 'Untitled';
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength).trim() + '...';
};

// UPDATED: Enhanced Optimized Image Component with better error handling
const OptimizedImage = React.memo(({ item, mode }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const imageUrl = useMemo(() => {
    if (imageError) {
      console.log('❌ Using placeholder due to error for item:', item.ad_id);
      return PLACEHOLDER_IMAGE;
    }
    
    let url = item.imageUrl || item.thumbnail;
    console.log('🖼️ Processing image URL for item', item.ad_id, ':', url);
    
    if (!url) {
      console.log('❌ No image URL found for item:', item.ad_id);
      return PLACEHOLDER_IMAGE;
    }
    
    // Handle relative URLs - make them absolute
    if (url.startsWith('/')) {
      const baseUrl = `${process.env.REACT_APP_USER_MANAGEMENT_API_BASE_URL}`;
      url = baseUrl + url;
      console.log('🔗 Converting relative URL to absolute:', url);
    }
    
    // Handle URLs that don't start with http/https but aren't data URLs
    if (!url.startsWith('http') && !url.startsWith('data:')) {
      console.log('⚠️ URL missing protocol, adding https:', url);
      url = `https://${url}`;
    }
    
    return url;
  }, [item.imageUrl, item.thumbnail, item.ad_id, imageError]);

  const handleImageError = useCallback((e) => {
    console.error('❌ Image failed to load:', imageUrl, 'for item:', item.ad_id);
    console.error('Error details:', e.target?.src);
    
    if (!imageError) {
      setImageError(true);
    }
    setIsLoading(false);
  }, [imageUrl, item.ad_id, imageError]);

  const handleImageLoad = useCallback(() => {
    console.log('✅ Image loaded successfully:', imageUrl, 'for item:', item.ad_id);
    setIsLoading(false);
  }, [imageUrl, item.ad_id]);

  // If no image URL available at all
  if (!item.imageUrl && !item.thumbnail) {
    return (
      <Box
        sx={{
          height: mode === 0 ? "200px" : "150px",
          bgcolor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9e9e9e',
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px'
        }}
      >
        <Typography variant="h4" sx={{ mb: 1, opacity: 0.5 }}>📷</Typography>
        <Typography variant="body2">No Image</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: mode === 0 ? "200px" : "150px" }}>
      {isLoading && !imageError && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f5f5f5',
            zIndex: 2,
            borderRadius: '4px'
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      
      <img
        src={imageUrl}
        alt={item.name || 'Product image'}
        className="w-100"
        loading="lazy"
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{ 
          height: mode === 0 ? "200px" : "150px", 
          objectFit: "cover",
          backgroundColor: '#f5f5f5',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease',
          borderRadius: '4px'
        }}
      />
    </Box>
  );
});

// Enhanced Payment Method Icons
const PaymentMethodIcon = React.memo(({ method, paypalId, venmoId }) => {
  const iconStyle = { width: '20px', height: '20px', marginRight: '4px' };
  
  switch (method?.toLowerCase()) {
    case 'venmo':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <AccountBalanceWallet sx={iconStyle} />
          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
            Venmo{venmoId ? `: @${venmoId}` : ''}
          </Typography>
        </Box>
      );
    case 'paypal':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <Payment sx={iconStyle} />
          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
            PayPal{paypalId ? `: ${paypalId}` : ''}
          </Typography>
        </Box>
      );
    case 'cash':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Payment sx={iconStyle} />
          <Typography variant="caption">Cash</Typography>
        </Box>
      );
    default:
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Payment sx={iconStyle} />
          <Typography variant="caption">Cash</Typography>
        </Box>
      );
  }
});

// FIXED: Enhanced Wishlist Item Card Component with proper favorite icon functionality
const WishlistCard = React.memo(({ item, mode, onItemClick, onRemoveFromWishlist, onToggleFavorite, getStatusColor, renderSubcategories }) => {
  const handleCardClick = useCallback(() => {
    onItemClick(item.ad_id);
  }, [item.ad_id, onItemClick]);

  const handleRemoveClick = useCallback(() => {
    return onRemoveFromWishlist(item.id, item.ad_id);
  }, [item.id, item.ad_id, onRemoveFromWishlist]);

  // FIXED: Add favorite toggle handler
  const handleFavoriteClick = useCallback((e) => {
    e.stopPropagation(); // Prevent card click
    onToggleFavorite(item.id, item.ad_id, item.is_wishlisted);
  }, [item.id, item.ad_id, item.is_wishlisted, onToggleFavorite]);

  const isSold = item.status === "Sold";

  if (mode === 0) {
    return (
      <div className="h-100">
        <div 
          className="product-card" 
          onClick={handleCardClick} 
          style={{ 
            cursor: 'pointer',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            overflow: 'hidden',
            opacity: isSold ? 0.8 : 1,
            filter: isSold ? 'grayscale(20%)' : 'none',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
          }}
        >
          <div className="product-media" style={{ position: 'relative' }}>
            <div className="product-img">
              <OptimizedImage item={item} mode={mode} />
            </div>
            
            <span className={`badge ${getStatusColor(item.status)}`} style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '700',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {item.status}
            </span>

            {/* FIXED: Favorite icon with proper click handler */}
            <IconButton
              onClick={handleFavoriteClick}
              size="small"
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                backgroundColor: item.is_wishlisted ? '#e91e63' : 'rgba(255, 255, 255, 0.9)',
                color: item.is_wishlisted ? 'white' : '#e91e63',
                minWidth: '32px',
                height: '32px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                zIndex:'9999',
                '&:hover': {
                  backgroundColor: item.is_wishlisted ? '#d81b60' : 'rgba(255, 255, 255, 1)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {item.is_wishlisted ? 
                <Favorite sx={{ fontSize: '16px' }} /> : 
                <FavoriteBorder sx={{ fontSize: '16px' }} />
              }
            </IconButton>

            {isSold && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(255, 193, 7, 0.95)',
                  color: '#000',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontWeight: '700',
                  fontSize: '14px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  letterSpacing: '1px'
                }}
              >
                SOLD OUT
              </Box>
            )}
          </div>
          
          <div className="product-content" style={{ padding: '20px' }}>
            <p className="date" style={{ 
              color: '#666', 
              fontSize: '12px', 
              margin: '0 0 8px 0',
              fontWeight: '500'
            }}>
              Added: {item.formattedDate}
            </p>
            <p className='price' style={{ 
              fontSize: '20px', 
              fontWeight: '700', 
              color: isSold ? '#ff9800' : '#2e7d32', 
              margin: '0 0 12px 0',
              textDecoration: isSold ? 'line-through' : 'none'
            }}>
              <b>$</b> {item.price}
              {isSold && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '12px', 
                  color: '#ff9800',
                  textDecoration: 'none'
                }}>
                  SOLD
                </span>
              )}
            </p>
            <h5 
              style={{ 
                margin: '0 0 12px 0', 
                fontSize: '16px', 
                fontWeight: '600',
                color: isSold ? '#666' : 'inherit'
              }}
              title={item.name}
            >
              {truncateTitle(item.name, 30)}
            </h5>
            <p className="text-truncate" style={{ 
              margin: '0 0 16px 0', 
              color: isSold ? '#999' : '#666',
              fontSize: '14px',
              lineHeight: '1.4'
            }}>{item.description}</p>
            
            {renderSubcategories(item.subcategories)}

            <div className="mt-3">
              <RemoveButton
                onRemove={handleRemoveClick}
                itemName={item.name}
                variant="outlined"
                size="small"
                fullWidth={true}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-card standard" style={{ 
      display: 'flex', 
      border: '1px solid #e0e0e0', 
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '16px',
      opacity: isSold ? 0.8 : 1,
      filter: isSold ? 'grayscale(20%)' : 'none',
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    }}>
      <div className="product-media" style={{ position: 'relative', width: '200px', flexShrink: 0 }}>
        <div className="product-img">
          <OptimizedImage item={item} mode={mode} />
          
          <span className={`badge ${getStatusColor(item.status)}`} style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '700',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            {item.status}
          </span>

          {/* FIXED: Favorite icon with proper click handler */}
          <IconButton
            onClick={handleFavoriteClick}
            size="small"
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              backgroundColor: item.is_wishlisted ? '#e91e63' : 'rgba(255, 255, 255, 0.9)',
              color: item.is_wishlisted ? 'white' : '#e91e63',
              minWidth: '32px',
              height: '32px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: item.is_wishlisted ? '#d81b60' : 'rgba(255, 255, 255, 1)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {item.is_wishlisted ? 
              <Favorite sx={{ fontSize: '16px' }} /> : 
              <FavoriteBorder sx={{ fontSize: '16px' }} />
            }
          </IconButton>

          {isSold && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255, 193, 7, 0.95)',
                color: '#000',
                padding: '6px 12px',
                borderRadius: '16px',
                fontWeight: '700',
                fontSize: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}
            >
              SOLD
            </Box>
          )}
        </div>
      </div>
      
      <div className="product-content" style={{ 
        flex: 1, 
        padding: '20px'
      }}>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <h5
            onClick={handleCardClick}
            style={{ 
              cursor: 'pointer', 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: '600',
              color: isSold ? '#666' : 'inherit'
            }}
            title={item.name}
          >
            {truncateTitle(item.name, 35)}
          </h5>
          <p className="text-muted small" style={{ 
            margin: 0
          }}>Added: {item.formattedDate}</p>
        </div>
        
        <p className="text-truncate mb-3" style={{ 
          color: isSold ? '#999' : '#666'
        }}>{item.description}</p>
        
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <div>
            <strong>Price:</strong> 
            <span style={{ 
              color: isSold ? '#ff9800' : '#2e7d32', 
              fontWeight: 'bold',
              textDecoration: isSold ? 'line-through' : 'none',
              marginLeft: '4px'
            }}>
              ${item.price}
            </span>
            {isSold && (
              <span style={{ 
                marginLeft: '8px', 
                fontSize: '12px', 
                color: '#ff9800'
              }}>
                SOLD
              </span>
            )}
          </div>
          <div>
            <strong>Category:</strong> {item.category}
          </div>
          <div>
            <strong>Condition:</strong> {item.condition}
          </div>
          {item.subcategories && item.subcategories.length > 0 && (
            <div>
              <strong>Subcategories:</strong>
              {renderSubcategories(item.subcategories)}
            </div>
          )}
          <div>
            <strong>Shipping:</strong> {item.shipping}
          </div>
          <div>
            <PaymentMethodIcon 
              method={item.payment_method} 
              paypalId={item.paypal_id}
              venmoId={item.venmo_id}
            />
          </div>
        </div>

        <div className="mt-3">
          <RemoveButton
            onRemove={handleRemoveClick}
            itemName={item.name}
            variant="outlined"
            size="small"
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );
});

const FavoriteTab = () => {
  // Use ref to track if component is mounted (same as Events page)
  const isMountedRef = useRef(true);

  // REMOVED: Tab-related state - no more active/sold tabs
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mode, setMode] = useState(0);
  
  // ✅ UPDATED: Set default pagination to 10 items per page
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Changed from 15 to 10
  const [totalItems, setTotalItems] = useState(0); // Total items from server
  const [totalPages, setTotalPages] = useState(0); // Total pages from server
  
  const navigate = useNavigate();

  // Mapping objects
  const shippingMethodMap = useMemo(() => ({
    1: "Cash On Delivery",
    2: "Free Shipping", 
    3: "Express Shipping",
  }), []);

  const paymentMethodMap = useMemo(() => ({
    1: "cash",
    2: "venmo", 
    3: "paypal",
  }), []);

  const conditionMap = useMemo(() => ({
    1: "New",
    2: "Used - Like New",
    3: "Used - Good", 
    4: "Used - Fair",
  }), []);

  const itemsPerPageOptions = useMemo(() => [
    { value: 10, label: "10 per page" },
    { value: 15, label: "15 per page" },
    { value: 20, label: "20 per page" },
    { value: 25, label: "25 per page" },
  ], []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case "Published":
        return "bg-success";
      case "Draft":
        return "bg-secondary";
      case "Active":
        return "bg-success";
      case "Sold":
        return "bg-warning";
      default:
        return "bg-dark";
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }, []);

  // Enhanced parseImages function with better debugging and error handling
  const parseImages = useCallback((imageData) => {
    console.log('🖼️ Parsing image data:', imageData, typeof imageData);
    
    if (!imageData) {
      console.log('❌ No image data provided');
      return [];
    }
    
    try {
      // Handle direct URL string
      if (typeof imageData === 'string') {
        // Check if it's a URL directly
        if (imageData.startsWith('http') || imageData.startsWith('/') || imageData.startsWith('data:')) {
          console.log('✅ Image data is direct URL:', imageData);
          return [imageData];
        }
        
        // Try to parse as JSON
        try {
          const parsed = JSON.parse(imageData);
          console.log('✅ Parsed JSON image data:', parsed);
          if (Array.isArray(parsed)) {
            return parsed.filter(img => img && (typeof img === 'string' || img.url || img.image_url));
          }
          return [parsed];
        } catch (parseError) {
          console.log('❌ Failed to parse as JSON, treating as URL:', imageData);
          return [imageData];
        }
      }
      
      // Handle array of images
      if (Array.isArray(imageData)) {
        console.log('✅ Image data is array:', imageData);
        return imageData.filter(img => {
          if (typeof img === 'string') return img;
          if (typeof img === 'object' && img) {
            return img.url || img.image_url || img.src || img.path;
          }
          return false;
        }).map(img => {
          if (typeof img === 'string') return img;
          return img.url || img.image_url || img.src || img.path;
        });
      }
      
      // Handle single object
      if (typeof imageData === 'object' && imageData) {
        console.log('✅ Image data is object:', imageData);
        const url = imageData.url || imageData.image_url || imageData.src || imageData.path;
        return url ? [url] : [];
      }
      
      return [];
    } catch (error) {
      console.error('❌ Error parsing images:', error, imageData);
      return [];
    }
  }, []);

  // SIMPLIFIED: Removed status filtering - fetch all wishlist items
  const fetchWishlist = useCallback(async (page = 1, limit = itemsPerPage, search = null) => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      setError(null);

      console.log('📡 Fetching wishlist with params:', { page, limit, search });
      
      // Use simple GET request - most reliable approach
      let response;
      try {
        response = await axiosInstance.get(`${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/wishlist`);
        console.log('✅ Wishlist API response:', response.data);
      } catch (apiError) {
        console.error('❌ API Error:', apiError);
        throw apiError;
      }
      
      if (!isMountedRef.current) return;

      if (response.data && (response.data.status === 200 || response.status === 200)) {
        const apiData = response.data.data || response.data;
        let allWishlistItems = apiData.wishlist || apiData.items || response.data.wishlist || [];
        
        console.log('📥 Raw wishlist data:', allWishlistItems.length, 'items');

        // CLIENT-SIDE FILTERING AND PAGINATION
        let filteredItems = [...allWishlistItems];
        
        // REMOVED: Status filtering - show all items
        
        // Filter by search
        if (search && search.trim()) {
          const searchTerm = search.trim().toLowerCase();
          filteredItems = filteredItems.filter(item => {
            const ad = item.ad || item;
            const name = (ad.name || ad.title || '').toLowerCase();
            const description = (ad.description || '').toLowerCase();
            const category = (ad.category_name || ad.category || '').toLowerCase();
            
            return name.includes(searchTerm) || 
                   description.includes(searchTerm) || 
                   category.includes(searchTerm);
          });
        }
        
        // Calculate pagination
        const totalCount = filteredItems.length;
        const totalPagesCount = Math.ceil(totalCount / limit);
        
        // Get items for current page
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const pageItems = filteredItems.slice(startIndex, endIndex);
        
        console.log('📊 Pagination info:', { 
          totalItems: totalCount, 
          totalPages: totalPagesCount, 
          currentPage: page,
          pageItems: pageItems.length,
          startIndex,
          endIndex
        });
        
        // Update pagination state
        setTotalItems(totalCount);
        setTotalPages(totalPagesCount);

        // Transform data
        const enrichedItems = pageItems.map((wishlistItem) => {
          const ad = wishlistItem.ad || wishlistItem;
          
          // Handle images
          const imageData = ad.images || ad.thumbnail || ad.image || ad.image_url || ad.imageUrl || ad.photo || ad.picture;
          const images = parseImages(imageData);
          let imageUrl = null;
          
          if (images.length > 0) {
            imageUrl = images[0];
            if (typeof imageUrl === 'object' && imageUrl) {
              imageUrl = imageUrl.url || imageUrl.image_url || imageUrl.src || imageUrl.path;
            }
          }

          // Handle subcategories
          let subcategories = [];
          if (ad.subcategories) {
            if (Array.isArray(ad.subcategories)) {
              subcategories = ad.subcategories.map(sc =>
                typeof sc === 'string' ? sc : sc.name || sc.title || sc.id || 'Unnamed'
              );
            } else if (typeof ad.subcategories === 'string') {
              subcategories = [ad.subcategories];
            } else if (typeof ad.subcategories === 'object') {
              subcategories = [ad.subcategories.name || ad.subcategories.title || 'Unnamed'];
            }
          }

          return {
            ...wishlistItem,
            id: wishlistItem.id,
            ad_id: ad.id || ad.ad_id,
            name: ad.name || ad.title || 'Untitled',
            description: ad.description || 'No description',
            price: ad.price || 0,
            category: ad.category_name || ad.category || "Unknown",
            shipping: shippingMethodMap[ad.shipping_method || ad.shipping_option] || "Unknown",
            payment_method: paymentMethodMap[ad.payment_methods || ad.payment_method] || "cash",
            condition: conditionMap[ad.condition] || "Unknown",
            paypal_id: ad.paypal_id || null,
            venmo_id: ad.venmo_id || null,
            imageUrl: imageUrl,
            thumbnail: imageUrl,
            status: ad.is_sold ? "Sold" : (ad.is_draft === 1 ? "Draft" : "Active"),
            formattedDate: formatDate(wishlistItem.created_at || wishlistItem.posted_on),
            subcategories: subcategories,
            is_wishlisted: true
          };
        });

        setItems(enrichedItems);
        
        // Show appropriate messages
        if (totalCount === 0 && page === 1) {
          if (search) {
            toast.info(`No items found matching "${search}"`, {
              position: "top-center",
              autoClose: 3000,
            });
          } else {
            toast.info('Your wishlist is empty. Start adding items! ❤️', {
              position: "top-center",
              autoClose: 3000,
            });
          }
        } else {
          console.log(`✅ Loaded page ${page} of ${totalPagesCount} (${enrichedItems.length} items shown, ${totalCount} total)`);
        }
        
      } else {
        throw new Error(response.data?.message || 'Failed to fetch wishlist');
      }
      
    } catch (err) {
      console.error('❌ Error fetching wishlist:', err);
      
      let errorMessage = 'Failed to load wishlist';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [shippingMethodMap, paymentMethodMap, conditionMap, formatDate, parseImages, itemsPerPage]);

  // FIXED: Add toggle favorite functionality
  const toggleFavorite = useCallback(async (wishlistId, adId, currentStatus) => {
    if (!adId || !isMountedRef.current) return;

    try {
      console.log('💖 Toggling favorite:', { wishlistId, adId, currentStatus });

      if (currentStatus) {
        // Remove from wishlist (same as existing remove functionality)
        await removeFromWishlist(wishlistId, adId);
      } else {
        // Add to wishlist
        let response;
        try {
          response = await axiosInstance.post(`${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/wishlist`, {
            ad_id: Number(adId)
          });
        } catch (postError) {
          console.log('Add to wishlist failed, trying alternative method...');
          response = await axiosInstance.put(`${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/wishlist`, {
            ad_id: Number(adId),
            action: 'add'
          });
        }

        if (!isMountedRef.current) return;

        console.log('📥 Add to wishlist response:', response.data);

        if (response.data?.status === 200 || response.status === 200) {
          // Refresh current page
          await fetchWishlist(currentPage, itemsPerPage, searchTerm);
          
          toast.success('Added to wishlist! ❤️', {
            position: "top-center",
            autoClose: 2000,
          });
        } else {
          throw new Error(response.data?.message || 'Failed to add to wishlist');
        }
      }
    } catch (err) {
      console.error('❌ Error toggling favorite:', err);

      let errorMessage = 'Failed to update wishlist';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  }, [currentPage, itemsPerPage, searchTerm, fetchWishlist]);

  // Remove item from wishlist (following Events page pattern)
  const removeFromWishlist = useCallback(async (wishlistId, adId) => {
    if (!wishlistId || !isMountedRef.current) return;

    try {
      console.log('📤 Removing from wishlist:', { wishlistId, adId });

      // Try different removal strategies
      let response;
      
      // Strategy 1: DELETE with ad_id in body
      try {
        response = await axiosInstance.delete(`${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/wishlist`, {
          data: { ad_id: Number(adId) }
        });
      } catch (deleteError) {
        console.log('Strategy 1 failed, trying strategy 2...');
        
        // Strategy 2: POST with remove action
        try {
          response = await axiosInstance.post(`${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/wishlist`, {
            ad_id: Number(adId),
            action: 'remove'
          });
        } catch (postError) {
          console.log('Strategy 2 failed, trying strategy 3...');
          
          // Strategy 3: DELETE with wishlist ID in URL
          response = await axiosInstance.delete(`${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/wishlist/${wishlistId}`);
        }
      }

      if (!isMountedRef.current) return;

      console.log('📥 Remove API Response:', response.data);

      if (response.data?.status === 200 || response.status === 200) {
        // After removing, refresh the current page
        await fetchWishlist(currentPage, itemsPerPage, searchTerm);
        
        toast.success('Removed from wishlist! 💔', {
          position: "top-center",
          autoClose: 2000,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to remove from wishlist');
      }
    } catch (err) {
      console.error('❌ Error removing from wishlist:', err);

      let errorMessage = 'Failed to remove from wishlist';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  }, [currentPage, itemsPerPage, searchTerm, fetchWishlist]);

  // SIMPLIFIED: Initialize component - removed tab dependency
  useEffect(() => {
    const initializeData = async () => {
      console.log('🚀 Initializing FavoriteTab component');
      setCurrentPage(1); // Ensure we start on page 1
      await fetchWishlist(1, itemsPerPage, searchTerm);
    };

    initializeData();
  }, [fetchWishlist, itemsPerPage]); // Removed tabValue dependency

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // SIMPLIFIED: Handle search - removed tab dependency
  const handleSearch = useCallback(async (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
    
    await fetchWishlist(1, itemsPerPage, value);
  }, [itemsPerPage, fetchWishlist]);

  // SIMPLIFIED: Handle page change - removed tab dependency
  const handlePageChange = useCallback(async (event, newPage) => {
    console.log('📄 Page change requested:', { currentPage, newPage, totalPages });
    
    if (newPage === currentPage || newPage < 1 || newPage > totalPages) {
      console.log('❌ Invalid page change request');
      return;
    }
    
    setCurrentPage(newPage);
    
    await fetchWishlist(newPage, itemsPerPage, searchTerm);
    
    // Smooth scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, totalPages, itemsPerPage, searchTerm, fetchWishlist]);

  // SIMPLIFIED: Handle items per page change - removed tab dependency
  const handleItemsPerPageChange = useCallback(async (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    
    await fetchWishlist(1, newItemsPerPage, searchTerm);
  }, [searchTerm, fetchWishlist]);

  // SIMPLIFIED: Since we're showing all items, no need for tab-based filtering
  const filteredItems = items; // Show all items

  // Since we're doing server-side pagination, use filteredItems directly
  const paginatedItems = filteredItems; // API already returns paginated results

  const renderSubcategories = useCallback((subcategories) => {
    if (!subcategories || subcategories.length === 0) {
      return null;
    }

    return (
      <div className="subcategories-container" style={{ 
        marginTop: '8px'
      }}>
        {subcategories.map((subcat, index) => (
          <Chip
            key={index}
            label={subcat}
            size="small"
            style={{ 
              marginRight: '4px', 
              marginBottom: '4px',
              fontWeight: '500'
            }}
            variant="outlined"
          />
        ))}
      </div>
    );
  }, []);

  // FIXED: Updated handleItemClick to navigate to favorites details page
  const handleItemClick = useCallback((itemId) => {
    navigate(`/myaccount/favorites/details/${itemId}`);
  }, [navigate]);

  // Loading state following Events page pattern

  // Error state following Events page pattern
  if (error && !items.length) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #E8EAF6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ padding: '32px', maxWidth: '400px', margin: '0 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', color: '#f44336', marginBottom: '16px' }}>❌</div>
          <Alert severity="error" style={{ marginBottom: '24px' }}>
            <strong>Error!</strong> {error}
          </Alert>
          <Button
            variant="contained"
            color="primary" 
            onClick={() => {
              fetchWishlist(1, itemsPerPage, searchTerm);
            }}
            className="!w-fit !px-3 !h-10"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Grid container spacing={2}>
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body">
                {/* Error display following Events page pattern */}
                {error && items.length > 0 && (
                  <Alert severity="error" style={{ marginBottom: '24px' }} onClose={() => setError(null)}>
                    <strong>Error!</strong> {error}
                  </Alert>
                )}

                {/* UPDATED: Header following Events page pattern */}
                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                  <div className="d-flex align-items-center">
                    <h3 className="mb-2">My Wishlist</h3>
                  </div>
                  <div className="d-flex align-items-center flex-wrap mb-1">
                    <Typography variant="subtitle1" color="textSecondary">
                      {totalItems} total item{totalItems !== 1 ? 's' : ''} in your wishlist
                      {loading && <span style={{ marginLeft: '8px' }}>⏳</span>}
                    </Typography>
                  </div>
                </div>

                {/* UPDATED: Search and Controls - REMOVED tab buttons */}
                <div className="row filter-panel mt-4">
                  <div className="col-md-6">
                    <div className="serarch-bar">
                      <Search
                        value={searchTerm}
                        handleInputChange={(e) => handleSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="w-full flex items-center justify-end gap-2">
                      {/* Items per page dropdown */}
                      <FormControl size="small" className="me-3" style={{ minWidth: 120 }}>
                        <InputLabel style={{ lineHeight: "14px" }}>
                          Per Page
                        </InputLabel>
                        <Select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          label="Per Page"
                          disabled={loading}
                          IconComponent={(props) => (
                            <ArrowDropDown
                              {...props}
                              style={{ color: "#c942b1" }}
                            />
                          )}
                          style={{ height: 40 }}
                        >
                          {itemsPerPageOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      {/* View mode buttons */}
                      <Button
                        variant={mode === 0 ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => setMode(0)}
                        className="!min-w-fit !px-2 !h-10"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path fillRule="evenodd" clipRule="evenodd" d="M2 2H11V11.2657H2V2ZM4 4V9.26573H9V4H4Z" fill="currentColor" />
                          <path fillRule="evenodd" clipRule="evenodd" d="M13 2H22V11H13V2ZM15 4V9H20V4H15Z" fill="currentColor" />
                          <path fillRule="evenodd" clipRule="evenodd" d="M2 13H11V22H2V13ZM4 15V20H9V15H4Z" fill="currentColor" />
                          <path fillRule="evenodd" clipRule="evenodd" d="M13 13H22V22H13V13ZM15 15V20H20V15H15Z" fill="currentColor" />
                        </svg>
                      </Button>
                      <Button
                        variant={mode === 1 ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => setMode(1)}
                        className="!min-w-fit !px-2 !h-10"
                      >
                        <svg viewBox="0 0 512 512" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                          <path d="M48 208C21.49 208 0 229.5 0 256s21.49 48 48 48S96 282.5 96 256S74.51 208 48 208zM48 368C21.49 368 0 389.5 0 416s21.49 48 48 48S96 442.5 96 416S74.51 368 48 368zM48 48C21.49 48 0 69.49 0 96s21.49 48 48 48S96 122.5 96 96S74.51 48 48 48zM192 128h288c17.67 0 32-14.33 32-31.1S497.7 64 480 64H192C174.3 64 160 78.33 160 95.1S174.3 128 192 128zM480 224H192C174.3 224 160 238.3 160 256s14.33 32 32 32h288c17.67 0 32-14.33 32-32S497.7 224 480 224zM480 384H192c-17.67 0-32 14.33-32 32s14.33 32 32 32h288c17.67 0 32-14.33 32-32S497.7 384 480 384z" fill="currentColor" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Results info */}
                {!loading && !error && (
                  <div className="row mt-3 mb-3">
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <Typography variant="body2" color="textSecondary">
                          Showing {paginatedItems.length} of {totalItems} items
                          {searchTerm && ` matching "${searchTerm}"`}
                          {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                        </Typography>
                        
                        {totalPages > 1 && (
                          <Typography variant="caption" color="textSecondary">
                            Page {currentPage} of {totalPages} • {itemsPerPage} per page
                          </Typography>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="row">
                  {loading ? (
                    <div className="w-full flex justify-center items-center py-10">
                      <img src={loadingGif} alt="Loading..." className="loading-icon-img" />
                    </div>
                  ) : !error && paginatedItems.length === 0 ? (
                    <div className="col-12 text-center py-5">
                      <div style={{
                        padding: '64px 32px',
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '16px',
                        border: '1px solid #e9ecef',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ 
                          fontSize: '64px', 
                          color: '#4caf50', 
                          marginBottom: '24px' 
                        }}>
                          💚
                        </div>
                        <h4 style={{ 
                          fontSize: '32px', 
                          fontWeight: '700', 
                          color: '#333', 
                          marginBottom: '16px'
                        }}>
                          {totalItems === 0 ? "No items in wishlist" : "No items found matching your search"}
                        </h4>
                        <p style={{ 
                          fontSize: '16px', 
                          color: '#666', 
                          marginBottom: '32px', 
                          maxWidth: '400px', 
                          margin: '0 auto 32px auto',
                          lineHeight: '1.5'
                        }}>
                          {totalItems === 0 
                            ? "Items you add to your wishlist will appear here!"
                            : `Try adjusting your search term or check other pages. ${totalItems} items total.`
                          }
                        </p>
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {totalItems === 0 && (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => navigate('/favorites')}
                              startIcon={<span>❤️</span>}
                              className="!w-fit !px-6 !h-12"
                            >
                              Browse Marketplace
                            </Button>
                          )}
                          {totalItems > 0 && (
                            <>
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => handleSearch('')}
                                startIcon={<Refresh />}
                                className="!w-fit !px-6 !h-12"
                                disabled={loading}
                              >
                                Clear Search
                              </Button>
                              {totalPages > 1 && currentPage > 1 && (
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  onClick={() => handlePageChange(null, 1)}
                                  className="!w-fit !px-6 !h-12"
                                  disabled={loading}
                                >
                                  Go to First Page
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    !error && paginatedItems.map((item) => (
                      <div className={mode === 0 ? "col-md-4 mb-4" : "col-12 mb-3"} key={item.id}>
                        <WishlistCard
                          item={item}
                          mode={mode}
                          onItemClick={handleItemClick}
                          onRemoveFromWishlist={removeFromWishlist}
                          onToggleFavorite={toggleFavorite}
                          getStatusColor={getStatusColor}
                          renderSubcategories={renderSubcategories}
                        />
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                {!loading && !error && totalItems > 0 && totalPages > 1 && (
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="d-flex flex-column align-items-center gap-3">
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          color="primary"
                          showFirstButton
                          showLastButton
                          size="large"
                          disabled={loading}
                          siblingCount={1}
                          boundaryCount={1}
                        />
                        
                        <div className="d-flex flex-column align-items-center gap-1">
                          <Typography variant="body2" color="textSecondary" align="center">
                            Page {currentPage} of {totalPages} • {totalItems} items total
                          </Typography>
                          <Typography variant="caption" color="textSecondary" align="center">
                            Showing {paginatedItems.length} items on this page
                          </Typography>
                        </div>

                        {totalPages > 5 && (
                          <div className="d-flex gap-2 flex-wrap justify-content-center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handlePageChange(null, 1)}
                              disabled={currentPage === 1 || loading}
                              className="!min-w-fit !px-2 !h-8"
                            >
                              First
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handlePageChange(null, Math.max(1, currentPage - 5))}
                              disabled={currentPage <= 5 || loading}
                              className="!min-w-fit !px-2 !h-8"
                            >
                              -5
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handlePageChange(null, Math.min(totalPages, currentPage + 5))}
                              disabled={currentPage > totalPages - 5 || loading}
                              className="!min-w-fit !px-2 !h-8"
                            >
                              +5
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handlePageChange(null, totalPages)}
                              disabled={currentPage === totalPages || loading}
                              className="!min-w-fit !px-2 !h-8"
                            >
                              Last
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bottom Summary */}
                {!loading && !error && totalItems > itemsPerPage && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                          <Typography variant="body2" color="textSecondary">
                            📊 {totalItems} total items • {itemsPerPage} per page • {totalPages} pages
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Client-side pagination • Use controls above to navigate
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default FavoriteTab;